import FlexSearch from 'flexsearch';
import { produce } from 'immer';
import { create } from 'zustand';
import * as Obj from '@pkg/utils/objects';
import * as Sort from '@pkg/utils/sort';

const InitialIndex = new FlexSearch.Index({
  tokenize: 'forward',
  context: true,
});

/**
 * @param {Index} index
 * @param {Map} mapped
 * @returns {Index}
 */
const updateIndex = (index, mapped) => {
  if (!Obj.isEmpty(index.register)) {
    for (const key in index.register) {
      if (!mapped.has(key)) {
        index.remove(key);
      }
    }
  }

  mapped.forEach(({ description }, key) => {
    if (!index.register?.[key]) {
      index.add(key, description);
    }
  });

  return index;
};

/**
 * @param {Object[]} list
 * @param {Index} index
 * @returns {Object}
 */
export const storeActivities = (list, index = InitialIndex) => {
  const mapped = new Map();
  const descriptions = {
    default: new Map(),
    lower: new Map(),
  };

  const sorted = list.sort(Sort.alpha('description'));
  sorted.forEach((item) => {
    const id = item.uuid;
    const description = item.description;

    mapped.set(id, item);
    descriptions.default.set(description, id);
    descriptions.lower.set(description.toLowerCase(), id);
  });

  return {
    descriptions,
    index: updateIndex(index, mapped),
    list: sorted,
    mapped,
  };
};

/**
 * @type {Function}
 * @param {Function} [getter]
 * @returns {any}
 */
const useStore = create((set, get) => ({
  descriptions: {
    default: new Map(),
    lower: new Map(),
  },
  list: [],
  index: InitialIndex,
  isLoading: true,
  mapped: new Map(),
  mutations: {},
  setLoading: (isLoading) => set(() => ({ isLoading })),
  setMutations: (mutations) => set(() => ({ mutations })),
  setActivities: (list) => set(({ index }) => storeActivities(list, index)),

  /**
   * @param {Object} activity
   * @param {String} activity.uuid
   * @param {String} activity.description
   * @returns {void}
   */
  add: ({ uuid, description }) =>
    set(
      produce((state) => {
        if (state.mapped.has(uuid)) {
          return;
        }

        state.descriptions.default.set(description, uuid);
        state.descriptions.lower.set(description.toLowerCase(), uuid);
        state.index.add(uuid, description);
        state.list.push({ uuid, description });
        state.list.sort(Sort.alpha('description'));
        state.mapped.set(uuid, { uuid, description });
      })
    ),

  /**
   * Removes an activity from the store. Used to roll back an optimistic `add`
   * when the server rejects the create (e.g. duplicate description).
   *
   * @param {String} uuid
   * @returns {void}
   */
  remove: (uuid) =>
    set(
      produce((state) => {
        const existing = state.mapped.get(uuid);
        if (!existing) {
          return;
        }

        state.descriptions.default.delete(existing.description);
        state.descriptions.lower.delete(existing.description.toLowerCase());
        state.index.remove(uuid);
        state.list = state.list.filter((item) => item.uuid !== uuid);
        state.mapped.delete(uuid);
      })
    ),

  /**
   * @param {String} uuid
   * @param {Object} attributes - Attributes to update (e.g. { order, process_id })
   * @returns {void}
   */
  update: (uuid, attributes) =>
    set(
      produce((state) => {
        const existing = state.mapped.get(uuid);
        if (!existing) {
          return;
        }

        // Update the mapped entry
        const updated = { ...existing, ...attributes };
        state.mapped.set(uuid, updated);

        // Update the list entry
        const listIndex = state.list.findIndex((item) => item.uuid === uuid);
        if (listIndex !== -1) {
          state.list[listIndex] = updated;
        }

        // Update description maps if description changed
        if (
          attributes.description &&
          attributes.description !== existing.description
        ) {
          state.descriptions.default.delete(existing.description);
          state.descriptions.lower.delete(existing.description.toLowerCase());
          state.descriptions.default.set(attributes.description, uuid);
          state.descriptions.lower.set(
            attributes.description.toLowerCase(),
            uuid
          );
          state.index.remove(uuid);
          state.index.add(uuid, attributes.description);
        }
      })
    ),

  /**
   * @param {String} description
   * @param {Boolean} strict
   * @returns {String|null}
   */
  getExisting: (description, strict = false) => {
    const map = get().descriptions[strict ? 'default' : 'lower'];
    const value = strict ? description : description.toLowerCase();
    return map.get(value) ?? null;
  },
}));

export default useStore;
